import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as actions from "../../../actions";
import CredentialsDisplay from "./CredentialsDisplay";
import { axiosPost } from "../../../util/axios";
import * as routers from "../../../util/routers";
import { ADMIN, CS, PARTNER } from "../../../constants/roles";

// Roles available for the required_roles picker (SUPERADMIN excluded — they access all apps by default)
const SELECTABLE_ROLES = [ADMIN, CS, PARTNER];

const ALLOWED_MIME = ["image/png", "image/jpeg", "image/svg+xml"];
const MAX_SIZE = 2 * 1024 * 1024;

function RolesPicker({ selected, onChange }) {
  const [open, setOpen] = useState(false);
  const available = SELECTABLE_ROLES.filter((r) => !selected.includes(r));

  const add = (role) => { onChange([...selected, role]); setOpen(false); };
  const remove = (role) => onChange(selected.filter((r) => r !== role));

  return (
    <div className="relative">
      <div
        className="flex flex-wrap items-center gap-1.5 min-h-[38px] w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm cursor-text"
        onClick={() => setOpen(true)}
      >
        {selected.map((role) => (
          <span key={role} className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 text-xs font-medium pl-2.5 pr-1 py-1 rounded">
            {role}
            <button type="button" onClick={(e) => { e.stopPropagation(); remove(role); }} className="text-slate-400 hover:text-slate-700 p-0.5 rounded-sm hover:bg-slate-200">
              <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </span>
        ))}
        {selected.length === 0 && <span className="text-slate-400 px-1">Add role...</span>}
      </div>
      {open && available.length > 0 && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <ul className="absolute z-20 bottom-full mb-1 w-full bg-white border border-slate-200 rounded-md shadow-lg py-1 max-h-40 overflow-y-auto">
            {available.map((role) => (
              <li key={role}>
                <button
                  type="button"
                  className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 text-slate-700"
                  onClick={() => add(role)}
                >
                  {role}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

export default function AddAppModal({ editingApp, onClose }) {
  const dispatch = useDispatch();
  const createdApp = useSelector((s) => s.apps.createdApp);
  const [name, setName] = useState(editingApp?.name ?? "");
  const [description, setDescription] = useState(editingApp?.description ?? "");
  const [logoUrl, setLogoUrl] = useState(editingApp?.logo_url ?? "");
  const [homeUrl, setHomeUrl] = useState(editingApp?.home_url ?? "");
  const [requiresAuth, setRequiresAuth] = useState(editingApp?.requires_auth ?? false);
  const [redirectUris, setRedirectUris] = useState(editingApp?.redirect_uris ?? [""]);
  const [requiredRoles, setRequiredRoles] = useState(editingApp?.required_roles ?? []);
  const [uploading, setUploading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showCreds, setShowCreds] = useState(false);

  const isEditing = !!editingApp;

  useEffect(() => {
    if (!submitted || !createdApp) return;
    if (createdApp.client_secret) setShowCreds(true); // auth app → show one-time credentials
    else onClose();                                    // discovery app → nothing to show
  }, [createdApp, submitted]);

  const handleLogoUpload = async (file) => {
    if (!file) return;
    if (!ALLOWED_MIME.includes(file.type)) return alert("PNG, JPEG, or SVG only");
    if (file.size > MAX_SIZE) return alert("Max 2 MB");
    setUploading(true);
    const form = new FormData();
    form.append("img", file);
    const res = await axiosPost(routers.oidcAppLogo, form);
    setUploading(false);
    if (res?.data?.Location) {
      setLogoUrl(res.data.Location);
    } else {
      alert("Upload failed");
    }
  };

  const updateRedirectUri = (idx, value) => {
    const next = [...redirectUris];
    next[idx] = value;
    setRedirectUris(next);
  };
  const addRedirectUri = () => setRedirectUris([...redirectUris, ""]);
  const removeRedirectUri = (idx) =>
    setRedirectUris(redirectUris.length > 1 ? redirectUris.filter((_, i) => i !== idx) : redirectUris);

  const submit = (e) => {
    e.preventDefault();
    // Reset any stale one-time credentials from a previous create/upgrade so the
    // effect above only reacts to THIS submission's response.
    dispatch(actions.CREATE_APP_RESPONSE(null));
    const dto = {
      name,
      description: description || undefined,
      logo_url: logoUrl || undefined,
      home_url: homeUrl,
      requires_auth: requiresAuth,
      ...(requiresAuth
        ? {
            redirect_uris: redirectUris.filter((u) => u.trim().length > 0),
            required_roles: requiredRoles.length > 0 ? requiredRoles : undefined,
          }
        : {}),
    };
    setSubmitted(true);
    if (isEditing) {
      const isUpgrade = requiresAuth && !editingApp.requires_auth;
      dispatch(actions.UPDATE_APP_REQ({ id: editingApp.id, dto, onSuccess: isUpgrade ? undefined : onClose }));
    } else {
      dispatch(actions.CREATE_APP_REQ({ dto }));
    }
  };

  if (showCreds && createdApp) {
    return <CredentialsDisplay app={createdApp} onClose={onClose} />;
  }

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-start justify-center z-50 pt-16 pb-10 overflow-y-auto">
      <form onSubmit={submit} className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4">
        <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-900">{isEditing ? "Edit App" : "Add App"}</h2>
            <p className="text-xs text-slate-500 mt-0.5">Register an internal app for single sign-on.</p>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700 hover:bg-slate-100 p-1.5 rounded-md">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="px-6 py-5 space-y-5 max-h-[calc(100vh-16rem)] overflow-y-auto">
          <div>
            <label className="block text-sm font-semibold text-slate-900 mb-1.5">Name <span className="text-red-500">*</span></label>
            <input value={name} onChange={(e) => setName(e.target.value)} required maxLength={128} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" />
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-900 mb-1.5">Description</label>
            <textarea rows="2" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={512} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" />
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-900 mb-1.5">Logo</label>
            <label
              className="border-2 border-dashed border-slate-300 rounded-md px-4 py-6 flex flex-col items-center justify-center cursor-pointer hover:border-slate-400 transition-colors"
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onDrop={(e) => { e.preventDefault(); e.stopPropagation(); handleLogoUpload(e.dataTransfer.files?.[0]); }}
            >
              <input type="file" accept="image/png,image/jpeg,image/svg+xml" onChange={(e) => handleLogoUpload(e.target.files?.[0])} className="sr-only" />
              {logoUrl ? (
                <img src={logoUrl} alt="" className="h-16 mb-2" />
              ) : (
                <svg className="h-8 w-8 text-slate-400 mb-2" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" />
                </svg>
              )}
              {uploading ? (
                <p className="text-xs text-slate-500">Uploading...</p>
              ) : (
                <>
                  <p className="text-sm text-slate-600"><span className="font-medium text-slate-700">Click to upload</span> or drag and drop</p>
                  <p className="text-xs text-slate-400 mt-1">PNG, JPG, SVG · up to 2 MB</p>
                </>
              )}
            </label>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-900 mb-1.5">Home URL <span className="text-red-500">*</span></label>
            <input value={homeUrl} onChange={(e) => setHomeUrl(e.target.value)} required placeholder="https://your-app.example/" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" />
            <p className="text-xs text-slate-500 mt-1.5">The Launch button opens this URL.</p>
          </div>

          <div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={requiresAuth}
                onChange={(e) => setRequiresAuth(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-slate-300"
              />
              <span>
                <span className="block text-sm font-semibold text-slate-900">Requires authentication (SSO)</span>
                <span className="block text-xs text-slate-500 mt-0.5">Issue a client ID and secret so this app can sign users in. Leave off for a launch-only catalog tile.</span>
              </span>
            </label>
            {isEditing && editingApp.requires_auth && !requiresAuth && (
              <div className="mt-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-900">
                Turning this off revokes the client ID and secret and clears the redirect URIs. Apps using SSO to sign in will stop working.
              </div>
            )}
          </div>

          {requiresAuth && (<>
          <div>
            <label className="block text-sm font-semibold text-slate-900 mb-1.5">Redirect URIs <span className="text-red-500">*</span></label>
            <div className="space-y-2">
              {redirectUris.map((uri, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <input
                    value={uri}
                    onChange={(e) => updateRedirectUri(idx, e.target.value)}
                    placeholder="https://your-app.example/auth/callback"
                    className="flex-1 px-3 py-2 border border-slate-300 rounded-md text-sm font-mono"
                  />
                  {redirectUris.length > 1 && (
                    <button type="button" onClick={() => removeRedirectUri(idx)} className="text-slate-400 hover:text-red-600 p-2">
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth="1.75" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button type="button" onClick={addRedirectUri} className="mt-2 text-sm font-medium text-blue-600 hover:text-blue-700">
              + Add another redirect URI
            </button>
            <p className="text-xs text-slate-500 mt-2">HTTPS required, except http://localhost for local dev.</p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-900 mb-1.5">Required roles</label>
            <RolesPicker selected={requiredRoles} onChange={setRequiredRoles} />
            <p className="text-xs text-slate-500 mt-1.5">Leave empty to make this app visible to all employees.</p>
          </div>
          </>)}
        </div>

        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-3">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-md">
            Cancel
          </button>
          <button type="submit" disabled={uploading} className="px-4 py-2 text-sm font-semibold text-white bg-slate-900 hover:bg-slate-800 disabled:bg-slate-400 rounded-md shadow-sm">
            {isEditing ? "Save changes" : "Create app"}
          </button>
        </div>
      </form>
    </div>
  );
}
